import { KITS } from "@golazo/shared";
import {
  ATTRIBUTE_KEYS,
  CAREER_VERSION,
  CLUBS_PER_DIVISION,
  DIVISIONS,
  FEED_LIMIT,
  MATCHDAYS,
  MOVERS,
  RETIRE_FORCED_AGE,
  RETIRE_OPTIONAL_AGE,
  TOP_TIER,
  type Attributes,
  type Career,
  type Club,
  type CompactResult,
  type MatchLine,
  type Offer,
  type PhoneMessage,
  type Relations,
  type SeasonState,
  type SeasonSummary,
  type Stadium,
} from "./model.js";
import { REWARD_KITS } from "./kits.js";
import { roundRobin, simulateScore, standings, type TableRow } from "./league.js";
import {
  afterMatchMessages,
  hallOfFameVerdict,
  kitMessage,
  OFFER_PITCHES,
  offerMessages,
  retirementMessages,
  seasonEndMessages,
  signedMessages,
  stayedMessages,
  streakMessage,
  welcomeMessages,
  type Draft,
  type Voices,
} from "./phone.js";
import {
  addAttributes,
  createPlayer,
  matchGrade,
  matchGrowth,
  overall,
  seasonAging,
  type MatchPerformance,
  type NewPlayer,
} from "./player.js";
import { PAPER_NAME } from "./names.js";
import { between, rngFor, shuffle } from "./rng.js";
import {
  OBJECTIVE_BONUS,
  OBJECTIVE_REWARD,
  RELATION_START,
  applyRelations,
  chooseObjective,
  growthFactor,
  objectiveProgress,
  objectiveVerdict,
  offerBoost,
  preMatchTalk,
  provokedOutcome,
  questionOf,
  relationsOf,
  squadBanter,
  squadOf,
  type TalkVoices,
} from "./phoneTalk.js";
import { CLUB_RATING_MAX, CLUB_RATING_MIN, TIER_RATING, clubById, createWorld } from "./world.js";

/**
 * La carrera, como maquina de estados pura.
 *
 *   newCareer → (beginMatch → recordMatch) x7 → fin de temporada ("offseason")
 *     → acceptOffer (fichas o te quedas) → temporada siguiente … → retire
 *
 * Todo devuelve una carrera NUEVA (no muta la que recibe) y la lista de cosas
 * que la pantalla tiene que celebrar (`CareerEvent`). Lo aleatorio sale de la
 * semilla y de donde se decide, asi que una carrera se puede rejugar en un test.
 */
export type CareerEvent =
  | { type: "kit"; kitId: string; reason: "season" | "swap" | "reward"; how?: string }
  | { type: "title"; tier: number }
  | { type: "promoted"; tier: number }
  | { type: "relegated"; tier: number }
  | { type: "seasonEnd"; summary: SeasonSummary }
  | { type: "growth"; delta: Attributes };

export interface CareerStep {
  career: Career;
  events: CareerEvent[];
}

export interface NewCareerInput extends NewPlayer {
  seed: number;
  /** El club con el que empiezas: uno de la Liga del Barrio, lo elige el jugador. */
  clubId: string;
}

/** Los clubes que se pueden elegir al empezar (sin crear la carrera). */
export function startingClubs(seed: number): Club[] {
  return createWorld(seed).filter((c) => c.tier === 0);
}

const clone = <T>(value: T): T => JSON.parse(JSON.stringify(value)) as T;

// ------------------------------------------------------------------ inicio

export function newCareer(input: NewCareerInput): Career {
  const clubs = createWorld(input.seed);
  const club = clubs.find((c) => c.id === input.clubId && c.tier === 0);
  if (!club) throw new Error(`Se empieza en la Liga del Barrio: ${input.clubId}`);
  const player = createPlayer(rngFor(input.seed, "player"), input);
  const career: Career = {
    version: CAREER_VERSION,
    seed: input.seed,
    player,
    clubs,
    season: startSeason(input.seed, clubs, 1, club.id),
    phase: "season",
    offers: [],
    nextTier: 0,
    history: [],
    titles: [],
    totals: { matches: 0, wins: 0, draws: 0, losses: 0, goals: 0, assists: 0, mvps: 0 },
    feed: [],
    nextMessageId: 1,
    swaps: [],
    earnedKits: [],
    peakOverall: overall(player),
    peakTier: 0,
    pending: null,
    relations: { coach: RELATION_START, squad: RELATION_START, fans: RELATION_START },
    objective: null,
    objectives: { done: 0, set: 0 },
    provoked: null,
  };
  const voices = voicesOf(career);
  push(career, welcomeMessages(voices, club.style));
  prepareNextMatch(career);
  return career;
}

function startSeason(seed: number, clubs: readonly Club[], number: number, clubId: string): SeasonState {
  const members = DIVISIONS.map((d) =>
    shuffle(
      rngFor(seed, "draw", number, d.tier),
      clubs.filter((c) => c.tier === d.tier).map((c) => c.id),
    ),
  );
  for (const m of members) {
    if (m.length !== CLUBS_PER_DIVISION) throw new Error(`Division con ${m.length} clubes`);
  }
  const club = clubById(clubs, clubId);
  return {
    number,
    members,
    results: DIVISIONS.map(() => []),
    matchday: 0,
    tier: club.tier,
    club: club.id,
    lines: [],
  };
}

// ------------------------------------------------------------------ consultas

export interface FixtureView {
  season: number;
  matchday: number;
  tier: number;
  club: Club;
  opponent: Club;
  /** Juegas en tu campo. */
  home: boolean;
  /** El estadio del que juega en casa. */
  stadium: Stadium;
}

export function nextFixture(career: Career): FixtureView | null {
  if (career.phase !== "season") return null;
  const s = career.season;
  if (s.matchday >= MATCHDAYS) return null;
  const members = s.members[s.tier]!;
  const me = members.indexOf(s.club);
  const game = roundRobin(CLUBS_PER_DIVISION)[s.matchday]!.find(([h, a]) => h === me || a === me)!;
  const home = game[0] === me;
  const opponent = clubById(career.clubs, members[home ? game[1] : game[0]]!);
  const club = clubById(career.clubs, s.club);
  return {
    season: s.number,
    matchday: s.matchday,
    tier: s.tier,
    club,
    opponent,
    home,
    stadium: (home ? club : opponent).stadium,
  };
}

/** Clasificacion de una division esta temporada. */
export function tableOf(career: Career, tier = career.season.tier): TableRow[] {
  return standings(career.season.members[tier]!, career.season.results[tier]!);
}

/** Los partidos de una jornada (jugados o no) de una division. */
export function matchdayOf(
  career: Career,
  tier: number,
  matchday: number,
): Array<{ home: Club; away: Club; score: [number, number] | null }> {
  const members = career.season.members[tier]!;
  const played = career.season.results[tier]![matchday] ?? [];
  return roundRobin(CLUBS_PER_DIVISION)[matchday]!.map(([h, a]) => {
    const r = played.find(([ph, pa]) => ph === h && pa === a);
    return {
      home: clubById(career.clubs, members[h]!),
      away: clubById(career.clubs, members[a]!),
      score: r ? [r[2], r[3]] : null,
    };
  });
}

export function canRetire(career: Career): boolean {
  return career.phase === "offseason" && career.player.age >= RETIRE_OPTIONAL_AGE;
}

/** Jugaste la temporada de los 35: ya no hay contrato que te salve. */
export function mustRetire(career: Career): boolean {
  return career.phase === "offseason" && career.player.age > RETIRE_FORCED_AGE;
}

// ------------------------------------------------------------------ partidos

/**
 * Se va a jugar el partido. Queda apuntado: si el juego se cierra a mitad, al
 * volver se da por jugado sin ti (`resolvePending`). El resultado es el
 * resultado — no hay repetir.
 */
export function beginMatch(career: Career): Career {
  const f = nextFixture(career);
  if (!f) return career;
  const next = clone(career);
  next.pending = { season: f.season, matchday: f.matchday };
  return next;
}

/** Si quedo un partido a medias (se cerro el juego), se juega sin ti. */
export function resolvePending(career: Career): CareerStep | null {
  if (!career.pending) return null;
  const f = nextFixture(career);
  if (!f || f.season !== career.pending.season || f.matchday !== career.pending.matchday) {
    const next = clone(career);
    next.pending = null;
    return { career: next, events: [] };
  }
  return recordMatch(career, null);
}

/**
 * Apunta tu partido y juega el resto de la jornada en todas las divisiones.
 * `report` null = no jugaste (se simula tu partido con tu equipo mermado).
 */
export function recordMatch(career: Career, report: MatchPerformance | null): CareerStep {
  const f = nextFixture(career);
  if (!f) return { career, events: [] };
  const c = clone(career);
  const s = c.season;
  const events: CareerEvent[] = [];
  const random = rngFor(c.seed, "md", s.number, s.matchday);

  // Tu marcador (orientado a TU equipo).
  let perf: MatchPerformance;
  if (report) {
    perf = report;
  } else {
    const [mine, theirs] = simulateScore(random, f.club.rating - 6, f.opponent.rating, f.tier);
    perf = {
      result: mine > theirs ? "win" : mine < theirs ? "loss" : "draw",
      goalsFor: mine,
      goalsAgainst: theirs,
      goals: 0,
      assists: 0,
      mvp: false,
      shots: 0,
      passes: 0,
      passesOk: 0,
      recoveries: 0,
    };
  }
  const grade = report ? matchGrade(c.player.position, perf) : 0;
  const line: MatchLine = {
    matchday: s.matchday,
    opponent: f.opponent.id,
    home: f.home,
    goalsFor: perf.goalsFor,
    goalsAgainst: perf.goalsAgainst,
    goals: perf.goals,
    assists: perf.assists,
    mvp: perf.mvp,
    grade,
    ...(report ? {} : { absent: true }),
  };
  s.lines.push(line);

  // La jornada entera, en las cuatro divisiones.
  const round = roundRobin(CLUBS_PER_DIVISION)[s.matchday]!;
  for (const division of DIVISIONS) {
    const tier = division.tier;
    const members = s.members[tier]!;
    const dayRandom = rngFor(c.seed, "day", s.number, s.matchday, tier);
    const played: CompactResult[] = round.map(([h, a]) => {
      const homeId = members[h]!;
      const awayId = members[a]!;
      if (tier === f.tier && (homeId === s.club || awayId === s.club)) {
        return f.home ? [h, a, perf.goalsFor, perf.goalsAgainst] : [h, a, perf.goalsAgainst, perf.goalsFor];
      }
      const [hg, ag] = simulateScore(
        dayRandom,
        clubById(c.clubs, homeId).rating,
        clubById(c.clubs, awayId).rating,
        tier,
      );
      return [h, a, hg, ag];
    });
    s.results[tier]![s.matchday] = played;
  }

  // El encargo del mister: se mira con tus numeros de verdad.
  const objective = c.objective && c.objective.season === s.number && c.objective.matchday === s.matchday ? c.objective : null;
  const objectiveDone = objective !== null && report !== null && objectiveProgress(objective, perf).done;
  if (objective) {
    objective.result = objectiveDone ? "done" : "failed";
    const count = c.objectives ?? { done: 0, set: 0 };
    c.objectives = { done: count.done + (objectiveDone ? 1 : 0), set: count.set + 1 };
  }

  // Crecer: lo que da el partido, mas o menos segun lo que te trabaja el
  // mister, y el empujon del encargo si lo cumpliste.
  let relations = relationsOf(c);
  if (report) {
    const delta = matchGrowth(c.player, perf, grade);
    const factor = growthFactor(relations);
    for (const key of ATTRIBUTE_KEYS) delta[key] = Math.round(delta[key] * factor * 100) / 100;
    if (objective && objectiveDone) {
      const key = OBJECTIVE_REWARD[objective.kind];
      const ageScale = c.player.age <= 28 ? 1 : c.player.age <= 31 ? 0.6 : 0.3;
      delta[key] = Math.round((delta[key] + OBJECTIVE_BONUS * c.player.potential * ageScale) * 100) / 100;
    }
    c.player.attributes = addAttributes(c.player.attributes, delta);
    c.peakOverall = Math.max(c.peakOverall, overall(c.player));
  }

  // Como te miran despues: el campo tambien habla.
  const drift: Partial<Relations> = { coach: 0, squad: 0, fans: 0 };
  const add = (key: keyof Relations, n: number): void => {
    drift[key] = (drift[key] ?? 0) + n;
  };
  if (!report) add("coach", -6);
  if (perf.result === "win") {
    add("fans", 2);
    add("squad", 1);
  } else if (perf.result === "loss") add("fans", -2);
  if (report && perf.mvp) {
    add("coach", 2);
    add("fans", 2);
  }
  if (report && grade >= 8) add("coach", 2);
  if (report && grade < 5) add("coach", -3);
  if (objective) add("coach", objectiveDone ? 8 : -4);
  const provoked = c.provoked && c.provoked.season === s.number && c.provoked.matchday === s.matchday;
  const provokedTalk = provoked ? provokedOutcome(perf.result, f.opponent.name) : null;
  if (provokedTalk) for (const [k, v] of Object.entries(provokedTalk.effect)) add(k as keyof Relations, v ?? 0);
  relations = applyRelations(relations, drift);
  c.relations = relations;
  c.provoked = null;

  // Contar.
  c.totals.matches++;
  if (perf.result === "win") c.totals.wins++;
  else if (perf.result === "draw") c.totals.draws++;
  else c.totals.losses++;
  c.totals.goals += perf.goals;
  c.totals.assists += perf.assists;
  if (perf.mvp) c.totals.mvps++;

  // Cambio de camiseta: ganas siendo el mejor, y te la llevas.
  if (report && perf.result === "win" && perf.mvp && !c.swaps.includes(f.opponent.id)) {
    c.swaps.push(f.opponent.id);
    events.push(...earnKit(c, f.opponent.kitId, "swap", { star: f.opponent.star, rival: f.opponent.name }));
  }
  events.push(...checkRewards(c));

  // El movil.
  const voices = voicesOf(c);
  push(
    c,
    afterMatchMessages(random, voices, {
      rival: f.opponent.name,
      result: perf.result,
      goalsFor: perf.goalsFor,
      goalsAgainst: perf.goalsAgainst,
      goals: perf.goals,
      assists: perf.assists,
      shots: perf.shots,
      mvp: perf.mvp,
      grade,
      position: c.player.position,
      absent: !report,
    }),
  );
  if (objective) push(c, [objectiveVerdict(objectiveDone, !report, objective)]);
  push(
    c,
    squadBanter(random, talkVoicesOf(c), {
      result: perf.result,
      goals: perf.goals,
      mvp: perf.mvp,
      grade,
      absent: !report,
      goalsAgainst: perf.goalsAgainst,
    }),
  );
  if (provokedTalk) push(c, [provokedTalk.draft]);
  const streak = streakMessage(
    voices,
    s.lines.map((l) => (l.goalsFor > l.goalsAgainst ? "win" : l.goalsFor < l.goalsAgainst ? "loss" : "draw")),
  );
  if (streak) push(c, [streak]);

  c.pending = null;
  s.matchday++;
  if (s.matchday >= MATCHDAYS) {
    events.push(...endSeason(c));
  } else {
    prepareNextMatch(c);
  }
  return { career: c, events };
}

// ------------------------------------------------------------------ fin de temporada

function endSeason(c: Career): CareerEvent[] {
  const events: CareerEvent[] = [];
  const s = c.season;
  const tables = DIVISIONS.map((d) => tableOf(c, d.tier));
  const myTable = tables[s.tier]!;
  const myRow = myTable.find((r) => r.club === s.club)!;
  const position = myTable.indexOf(myRow) + 1;
  const club = clubById(c.clubs, s.club);

  // Ascensos y descensos, en todas las divisiones.
  const moves = new Map<string, number>();
  for (const division of DIVISIONS) {
    const table = tables[division.tier]!;
    if (division.tier < TOP_TIER) {
      for (const row of table.slice(0, MOVERS)) moves.set(row.club, division.tier + 1);
    }
    if (division.tier > 0) {
      for (const row of table.slice(-MOVERS)) moves.set(row.club, division.tier - 1);
    }
  }
  const champion = position === 1;
  const promoted = moves.get(club.id) === s.tier + 1;
  const relegated = moves.get(club.id) === s.tier - 1;

  // La temporada, para el historial.
  const played = s.lines.filter((l) => !l.absent);
  const grade = played.length > 0 ? played.reduce((sum, l) => sum + l.grade, 0) / played.length : 5;
  const summary: SeasonSummary = {
    number: s.number,
    age: c.player.age,
    tier: s.tier,
    club: club.id,
    clubName: club.name,
    position,
    points: myRow.points,
    played: s.lines.length,
    goals: s.lines.reduce((sum, l) => sum + l.goals, 0),
    assists: s.lines.reduce((sum, l) => sum + l.assists, 0),
    mvps: s.lines.filter((l) => l.mvp).length,
    grade: Math.round(grade * 10) / 10,
    champion,
    promoted,
    relegated,
  };
  c.history.push(summary);
  events.push({ type: "seasonEnd", summary });
  if (champion) {
    c.titles.push({ season: s.number, tier: s.tier, club: club.id, clubName: club.name });
    events.push({ type: "title", tier: s.tier });
  }
  if (promoted) events.push({ type: "promoted", tier: s.tier + 1 });
  if (relegated) events.push({ type: "relegated", tier: s.tier - 1 });

  // Una temporada con su camiseta te la hace tuya.
  events.push(...earnKit(c, club.kitId, "season", { club: club.name }));

  // El mundo se mueve: ascensos, descensos y la fuerza de cada club.
  const drift = rngFor(c.seed, "drift", s.number);
  for (const other of c.clubs) {
    const to = moves.get(other.id);
    const change = to === undefined ? between(drift, -2, 2) : to > other.tier ? between(drift, 1, 4) : -between(drift, 1, 4);
    if (to !== undefined) other.tier = to;
    const [lo, hi] = TIER_RATING[other.tier]!;
    const mid = (lo + hi) / 2;
    // Tiende al centro de su division: nadie se queda de gallito para siempre.
    other.rating = Math.round(
      Math.max(CLUB_RATING_MIN, Math.min(CLUB_RATING_MAX, other.rating + change + (mid - other.rating) * 0.12)),
    );
  }
  c.nextTier = clubById(c.clubs, club.id).tier;

  // Envejecer (y cumplir años).
  const aging = seasonAging(c.player, grade, rngFor(c.seed, "aging", s.number));
  c.player.attributes = addAttributes(c.player.attributes, aging);
  c.player.age++;
  events.push({ type: "growth", delta: aging });
  c.peakOverall = Math.max(c.peakOverall, overall(c.player));
  events.push(...checkRewards(c));

  // Ofertas.
  c.offers = mustRetire({ ...c, phase: "offseason" }) ? [] : makeOffers(c, grade);
  c.phase = "offseason";

  const voices = voicesOf(c);
  push(
    c,
    seasonEndMessages(voices, {
      season: s.number,
      position,
      division: DIVISIONS[s.tier]!.name,
      nextDivision: DIVISIONS[c.nextTier]!.name,
      champion,
      promoted,
      relegated,
      bottomOfLadder: s.tier === 0 && position === CLUBS_PER_DIVISION,
      topTier: s.tier === TOP_TIER,
      goals: summary.goals,
      grade: summary.grade,
      age: c.player.age,
    }),
  );
  if (!mustRetire(c)) {
    push(
      c,
      offerMessages(
        c.offers.map((o) => ({ club: clubById(c.clubs, o.club).name, topTier: o.tier === TOP_TIER })),
        c.player.age,
      ),
    );
  }
  return events;
}

/**
 * Quien te quiere para la temporada que viene.
 *
 * Tu "nivel" es tu media corregida por como has jugado este año (una
 * temporada de 8 pesa) y castigada por la edad a partir de los 32. Los clubes
 * de la division de encima de la de tu club te fichan si tu nivel se acerca a
 * su fuerza; los de tu misma division, solo los bastante mejores que el tuyo.
 * Como mucho tres ofertas.
 *
 * Nunca dos divisiones por encima. Se permitia, y con una temporada buena a
 * los 17 llovian ofertas de Primera: del 3v3 del barrio al 11v11 de golpe, sin
 * pisar el 5v5 ni el 7v7. La escalera es la gracia; saltarse media no.
 */
function makeOffers(c: Career, seasonGrade: number): Offer[] {
  const random = rngFor(c.seed, "offers", c.season.number);
  const mine = clubById(c.clubs, c.season.club);
  // La fama tambien juega: una aficion que te corea hace que te llamen.
  const level =
    overall(c.player) + (seasonGrade - 6.3) * 6 - Math.max(0, c.player.age - 31) * 3 + offerBoost(relationsOf(c));
  const candidates = c.clubs.filter((club) => {
    if (club.id === mine.id) return false;
    const jump = club.tier - c.nextTier;
    if (jump < 0 || jump > 1) return false;
    const noise = random() * 4 - 2;
    if (jump === 0) return club.rating > mine.rating + 4 && level + noise >= club.rating - 4;
    return level + noise >= club.rating - 6;
  });
  return shuffle(random, candidates)
    .sort((a, b) => b.tier - a.tier || b.rating - a.rating)
    .slice(0, 3)
    .map((club, i) => ({ club: club.id, tier: club.tier, pitch: (c.season.number + i) % OFFER_PITCHES.length }));
}

// ------------------------------------------------------------------ fichar o quedarse

/** Fichar por un club de tus ofertas, o quedarte (`null`). Empieza la temporada siguiente. */
export function acceptOffer(career: Career, clubId: string | null): CareerStep {
  if (career.phase !== "offseason" || mustRetire(career)) return { career, events: [] };
  if (clubId !== null && !career.offers.some((o) => o.club === clubId)) {
    throw new Error(`No hay oferta del ${clubId}`);
  }
  const c = clone(career);
  const oldClub = clubById(c.clubs, c.season.club);
  const target = clubId ?? oldClub.id;
  c.season = startSeason(c.seed, c.clubs, c.season.number + 1, target);
  c.offers = [];
  c.phase = "season";
  c.peakTier = Math.max(c.peakTier, c.season.tier);
  const events = checkRewards(c);
  // Club nuevo: mister y vestuario nuevos, que no te conocen de nada. La fama
  // viaja contigo, a medias.
  if (clubId !== null && clubId !== oldClub.id) {
    const r = relationsOf(c);
    c.relations = { coach: RELATION_START, squad: RELATION_START - 5, fans: Math.round(RELATION_START + (r.fans - RELATION_START) / 2) };
  }
  const voices = voicesOf(c);
  if (clubId !== null) push(c, signedMessages(voices, clubById(c.clubs, target).style, oldClub.coach));
  else push(c, stayedMessages());
  prepareNextMatch(c);
  return { career: c, events };
}

// ------------------------------------------------------------------ retirada

export interface HallOfFameEntry {
  id: string;
  name: string;
  number: number;
  position: string;
  seasons: number;
  lastAge: number;
  matches: number;
  goals: number;
  assists: number;
  mvps: number;
  titles: Array<{ tier: number; clubName: string; season: number }>;
  clubs: string[];
  peakTier: number;
  peakOverall: number;
  /** Clave de `t()`: el epitafio. */
  verdict: string;
  retiredAt: number;
}

export function retire(career: Career, now: number): CareerStep & { entry: HallOfFameEntry } {
  const c = clone(career);
  const clubs: string[] = [];
  for (const h of c.history) if (clubs.at(-1) !== h.clubName) clubs.push(h.clubName);
  const primeraTitles = c.titles.filter((t) => t.tier === TOP_TIER).length;
  const entry: HallOfFameEntry = {
    id: `${c.seed}-${now}`,
    name: c.player.name,
    number: c.player.number,
    position: c.player.position,
    seasons: c.history.length,
    lastAge: c.player.age - 1,
    matches: c.totals.matches,
    goals: c.totals.goals,
    assists: c.totals.assists,
    mvps: c.totals.mvps,
    titles: c.titles.map((t) => ({ tier: t.tier, clubName: t.clubName, season: t.season })),
    clubs,
    peakTier: c.peakTier,
    peakOverall: c.peakOverall,
    verdict: hallOfFameVerdict(c.peakTier, primeraTitles),
    retiredAt: now,
  };
  c.phase = "retired";
  c.offers = [];
  push(c, retirementMessages(voicesOf(c), c.history.length));
  const events = checkRewards(c);
  return { career: c, entry, events };
}

// ------------------------------------------------------------------ camisetas

/** Da la camiseta si no la tenia ya esta carrera. */
function earnKit(
  c: Career,
  kitId: string,
  reason: "season" | "swap" | "reward",
  params: { club?: string; star?: string; rival?: string; how?: string; kitLabel?: string },
): CareerEvent[] {
  if (c.earnedKits.includes(kitId)) return [];
  c.earnedKits.push(kitId);
  push(c, [kitMessage(reason, { ...params, kit: params.kitLabel })]);
  return [{ type: "kit", kitId, reason, ...(params.how ? { how: params.how } : {}) }];
}

/** Los hitos que dan las camisetas de premio. */
function checkRewards(c: Career): CareerEvent[] {
  const t = c.totals;
  const titles = c.titles;
  const h = c.history;
  const promotedTwice = h.length >= 2 && h.at(-1)!.promoted && h.at(-2)!.promoted;
  const done: Record<string, boolean> = {
    burdeosOro: titles.length > 0,
    negroOro: c.peakTier >= TOP_TIER || c.season.tier >= TOP_TIER,
    blancoOro: titles.some((x) => x.tier === TOP_TIER),
    medianoche: t.goals >= 50,
    lava: t.goals >= 150,
    electrico: t.assists >= 50,
    arlequin: t.mvps >= 25,
    hielo: t.matches >= 100,
    radiactivo: promotedTwice,
    tricolor: c.phase === "retired",
  };
  const events: CareerEvent[] = [];
  for (const reward of REWARD_KITS) {
    if (done[reward.kitId]) {
      events.push(...earnKit(c, reward.kitId, "reward", { how: reward.how, kitLabel: KITS[reward.kitId]?.label ?? reward.kitId }));
    }
  }
  return events;
}

// ------------------------------------------------------------------ movil

function voicesOf(c: Career): Voices {
  const club = clubById(c.clubs, c.season.club);
  return { name: c.player.name, club: club.name, coach: club.coach };
}

function talkVoicesOf(c: Career): TalkVoices {
  const club = clubById(c.clubs, c.season.club);
  return { name: c.player.name, club: club.name, coach: club.coach, squad: squadOf(c.seed, club.id) };
}

/**
 * Antes de cada partido: el mister manda el informe del rival y el encargo, y
 * a veces alguien mas pide respuesta. Se llama al empezar la carrera, tras
 * cada jornada y al empezar temporada.
 */
function prepareNextMatch(c: Career): void {
  const f = nextFixture(c);
  if (!f) return;
  const random = rngFor(c.seed, "preview", f.season, f.matchday);
  const objective = chooseObjective(random, c.player.position, f.opponent.style, f.tier);
  c.objective = { season: f.season, matchday: f.matchday, ...objective };
  const last = c.season.lines.at(-1);
  push(
    c,
    preMatchTalk(random, talkVoicesOf(c), {
      rival: f.opponent.name,
      rivalId: f.opponent.id,
      rivalStyle: f.opponent.style,
      rivalStar: f.opponent.star,
      matchday: f.matchday,
      season: f.season,
      objective,
      lastResult: last ? (last.goalsFor > last.goalsAgainst ? "win" : last.goalsFor < last.goalsAgainst ? "loss" : "draw") : null,
    }),
  );
}

// ------------------------------------------------------------------ contestar

/**
 * Se puede contestar mientras dura la espera de ese partido: lo que te
 * preguntan antes de la jornada 3 ya no se contesta despues de jugarla (como
 * en la vida: el "¿vienes a cenar?" del jueves no se contesta el sabado).
 */
export function canAnswer(career: Career, message: PhoneMessage): boolean {
  return (
    career.phase === "season" &&
    message.ask !== undefined &&
    message.answer === undefined &&
    questionOf(message.ask) !== null &&
    message.season === career.season.number &&
    message.matchday === career.season.matchday
  );
}

/**
 * Contestar un mensaje. Se apunta la respuesta, se aplica lo que cambia, y
 * llegan dos burbujas: la tuya y la suya. Devuelve la carrera nueva y cuanto
 * ha cambiado cada relacion (para enseñarlo).
 */
export function answerMessage(
  career: Career,
  messageId: number,
  option: number,
): { career: Career; effect: Partial<Relations>; provoked: boolean } {
  const original = career.feed.find((m) => m.id === messageId);
  const question = questionOf(original?.ask);
  const choice = question?.options[option];
  if (!original || !question || !choice || !canAnswer(career, original)) return { career, effect: {}, provoked: false };
  const c = clone(career);
  const m = c.feed.find((x) => x.id === messageId)!;
  m.answer = option;
  c.relations = applyRelations(relationsOf(c), choice.effect);
  if (choice.provoke) c.provoked = { season: c.season.number, matchday: c.season.matchday };
  const params = { ...(m.params ?? {}), name: c.player.name, paper: PAPER_NAME };
  push(c, [
    { from: m.from, key: choice.say, mine: true },
    { from: m.from, key: choice.back, params, ...(m.who ? { who: m.who } : {}) },
  ]);
  return { career: c, effect: choice.effect, provoked: choice.provoke === true };
}

function push(c: Career, drafts: readonly Draft[]): void {
  for (const d of drafts) {
    c.feed.push({ ...d, id: c.nextMessageId++, season: c.season.number, matchday: c.season.matchday });
  }
  if (c.feed.length > FEED_LIMIT) c.feed.splice(0, c.feed.length - FEED_LIMIT);
}

/** La media que se enseña en la ficha. */
/**
 * Cambiar el nombre del jugador. Tambien en los mensajes que ya tienes: si no,
 * el movil seguiria hablando de quien eras (el nombre va dentro de cada uno).
 */
export function renamePlayer(career: Career, raw: string): Career {
  const name = raw.trim().slice(0, 16);
  if (!name || name === career.player.name) return career;
  const c = clone(career);
  const old = c.player.name;
  c.player.name = name;
  for (const m of c.feed) {
    if (!m.params) continue;
    for (const [k, v] of Object.entries(m.params)) if (v === old) m.params[k] = name;
  }
  return c;
}

/** Como te llevas con el mister, el vestuario y la aficion (50 si la carrera es de antes). */
export function careerRelations(c: Career): Relations {
  return relationsOf(c);
}

export function careerOverall(c: Career): number {
  return overall(c.player);
}

/** Para comprobar que nadie se sale de la escala (tests). */
export function attributesInRange(a: Attributes): boolean {
  return ATTRIBUTE_KEYS.every((k) => a[k] >= 30 && a[k] <= 99);
}
